/** Operations dashboard + audit chain verification (doc 11 §4, §8). */
import {
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  AgentReportSection,
  AssuranceCommandCenter as AssuranceCommandCenterData,
  Localized,
} from '@servicesai/api-client';
import {
  DataTable,
  GlassButton,
  GlassCard,
  MetricCard,
  useI18n,
  type DataTableColumn,
} from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile, SectionCard, SegmentedTabs, StatusDot } from '../components/elevated/kit.js';
import { Icon, type IconName } from '../components/elevated/icons.js';
import { TableSkeleton } from '../components/elevated/states.js';
import { ServiceIcon } from '../components/ServiceIcon.js';

type AssuranceSlug = AssuranceCommandCenterData['slug'];

const ASSURANCE_AGENTS: { slug: AssuranceSlug; name: Localized }[] = [
  { slug: 'cyber-sentinel', name: { en: 'Cyber Sentinel', ar: 'حارس الأمن السيبراني' } },
  { slug: 'ai-governance', name: { en: 'AI Governance', ar: 'حوكمة الذكاء الاصطناعي' } },
  { slug: 'tech-ops', name: { en: 'Tech Ops', ar: 'العمليات التقنية' } },
  { slug: 'language-sentinel', name: { en: 'Language Sentinel', ar: 'حارس اللغة' } },
];

const ASSURANCE_WINDOWS = [
  { minutes: 1_440, label: { en: '24h', ar: '٢٤ ساعة' } },
  { minutes: 10_080, label: { en: '7d', ar: '٧ أيام' } },
  { minutes: 43_200, label: { en: '30d', ar: '٣٠ يوماً' } },
] as const;

/* Time-window switcher (plan point 19): one window drives the KPI tiles,
 * sparklines and every chart that carries dates client-side. */
type WindowKey = '24h' | '7d' | '30d';

const WINDOW_ORDER: WindowKey[] = ['24h', '7d', '30d'];
const WINDOW_MS: Record<WindowKey, number> = {
  '24h': 86_400_000,
  '7d': 604_800_000,
  '30d': 2_592_000_000,
};
const WINDOW_DAYS: Record<WindowKey, number> = { '24h': 1, '7d': 7, '30d': 30 };
const WINDOW_ASSURANCE_MINUTES: Record<WindowKey, number> = { '24h': 1_440, '7d': 10_080, '30d': 43_200 };

/* Lightweight row shapes for the extra dashboard reads (queues, handover,
 * approvals, services, audit) — cast from the generic client envelopes. */
interface QueueItemLite {
  id: string;
  stage: string;
  status: string;
  due_at: string | null;
  submitted_at: string | null;
}

interface HandoverLite {
  conversationId: string;
  channel: string;
  customerName: { en?: string; ar?: string } | null;
  phone: string | null;
  claimedById: string | null;
}

interface ApprovalLite {
  id: string;
  kind: string;
  maker_name: string;
  created_at: string;
}

interface ServiceLite {
  slug: string;
  name: Localized;
  category?: string;
  has_draft: boolean;
}

interface AuditItemLite {
  id: number;
  ts: string;
  actor: Record<string, unknown>;
  action: string;
  subject: Record<string, unknown>;
}

function isOverdueItem(item: QueueItemLite): boolean {
  return item.due_at !== null && new Date(item.due_at).getTime() < Date.now() && item.status !== 'approved';
}

/** Bilingual relative time: '12m ago' / «منذ ١٢ د» (plan point 23). */
const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

function relativeTime(iso: string, lang: 'en' | 'ar'): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  const digits = (value: number) =>
    lang === 'ar' ? String(value).replace(/\d/g, (digit) => AR_DIGITS[Number(digit)]!) : String(value);
  if (minutes < 1) return lang === 'ar' ? 'الآن' : 'just now';
  if (minutes < 60) return lang === 'ar' ? `منذ ${digits(minutes)} د` : `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return lang === 'ar' ? `منذ ${digits(hours)} س` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return lang === 'ar' ? `منذ ${digits(days)} ي` : `${days}d ago`;
}

/** Map an audit action onto the console icon set for the activity pulse. */
function activityIcon(action: string): IconName {
  if (action.startsWith('handover') || action.startsWith('conversation')) return 'handover';
  if (action.startsWith('approval')) return 'approvals';
  if (action.startsWith('service')) return 'services';
  if (action.startsWith('agent')) return 'agents';
  if (action.startsWith('payment')) return 'payments';
  if (action.startsWith('govsync')) return 'govsync';
  if (action.startsWith('document')) return 'documents';
  if (action.startsWith('housing')) return 'housing';
  if (action.startsWith('case') || action.startsWith('application') || action.startsWith('review')) return 'case';
  return 'pulse';
}

interface Summary {
  byState: { state: string; count: number }[];
  queueDepth: { stage: string; count: number }[];
  byChannel?: { channel: string; count: number }[];
  overrideRate: number | null;
  decisions: { decided: number; avg_hours: string | null };
  security?: {
    events24h: { severity: string; count: number }[];
    openIncidents: number;
    moderation7d: { action: string; count: number }[];
    securityScore: string | null;
  };
  assurance?: {
    agents: {
      slug: string;
      score: string | null;
      scoreLabel: { en: string; ar: string } | null;
      summary: { en: string; ar: string } | null;
      recommendations: { en: string; ar: string }[];
      observedAt: string | null;
      measurementStatus: 'measured' | 'not_measured';
    }[];
    openCritical: number;
    reportDelivery: {
      succeeded: number;
      failed: number;
      last_finished_at: string | null;
    };
  };
  notCompleted?: { drafts: number; abandoned: number };
  stageTat?: { stage: string; p50_hours: string | null; p90_hours: string | null; completed: number }[];
  breaches?: { total: number; severe: number };
  containmentRate?: number | null;
  volumeTrend?: { day: string; count: number }[];
  overrideByService?: { slug: string; name: { en: string; ar: string }; corrected: number; total: number; rate: number | null }[];
}

function assuranceWindowValue(
  commandCenter: AssuranceCommandCenterData | undefined,
  windowMinutes: number,
): string | number | null {
  if (!commandCenter) return null;
  for (const report of commandCenter.reports) {
    for (const binding of report.definition.bindings) {
      if (binding.windowMinutes !== windowMinutes || binding.aggregation !== 'count') continue;
      const metric = report.definition.sections.find(
        (section): section is Extract<AgentReportSection, { kind: 'metric' }> =>
          section.kind === 'metric' && section.bindingKey === binding.key,
      );
      const value = report.resolved[binding.key];
      if (metric && (typeof value === 'number' || typeof value === 'string')) return value;
    }
  }
  return null;
}

function AssuranceMiniTrend({ rows, name }: { rows: AssuranceCommandCenterData['trend']; name: string }) {
  const { l } = useI18n();
  if (rows.length < 2) {
    return <p className="py-2 text-[10px] text-ink-faint">{l({ en: 'Trend points not measured', ar: 'نقاط الاتجاه غير مقاسة' })}</p>;
  }
  const width = 180;
  const height = 42;
  const max = Math.max(1, ...rows.map((row) => row.count));
  const points = rows.map((row, index) => {
    const x = (index / Math.max(1, rows.length - 1)) * width;
    const y = height - 5 - (row.count / max) * (height - 10);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${name}: ${l({ en: 'measured signal trend', ar: 'اتجاه الإشارات المقاسة' })}`} className="h-11 w-full">
      <polyline points={points} fill="none" stroke="#0173ab" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <title>{rows.map((row) => `${row.bucket}: ${row.count}`).join('; ')}</title>
    </svg>
  );
}

function BarChart({ rows, labelKey }: { rows: { [k: string]: string | number }[]; labelKey: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  const max = Math.max(1, ...rows.map((r) => Number(r.count) || 0));
  const total = rows.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
  const colors = ['bg-seablue-600', 'bg-aegold-600', 'bg-aegreen-600', 'bg-camel-600', 'bg-aeblack-500', 'bg-aered-500'];
  const selectedRow = rows.find((row) => String(row[labelKey]) === selected);
  return (
    <div>
      {rows.length === 0 && <div className="text-xs text-ink-faint">—</div>}
      {selectedRow && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-seablue-100 bg-seablue-50 px-3 py-2">
          <span className="text-xs font-bold text-ink">{String(selectedRow[labelKey]).replaceAll('_', ' ')}</span>
          <span className="font-mono text-sm font-extrabold text-oasis">
            {Number(selectedRow.count)} <span className="text-[10px] font-semibold">· {total > 0 ? Math.round(Number(selectedRow.count) / total * 100) : 0}%</span>
          </span>
        </div>
      )}
      <div className="space-y-2.5">
      {rows.map((row, index) => {
        const count = Number(row.count) || 0;
        const pct = Math.round((count / max) * 100);
        const share = total > 0 ? Math.round((count / total) * 100) : 0;
        const key = String(row[labelKey]);
        return (
          <button
            type="button"
            key={key}
            aria-pressed={selected === key}
            onClick={() => setSelected((current) => current === key ? null : key)}
            className={`block w-full rounded-lg p-1.5 text-start transition hover:bg-aeblack-50 ${selected === key ? 'bg-aeblack-50 ring-1 ring-seablue-200' : ''}`}
          >
            <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
              <span className="min-w-0 truncate font-semibold text-ink-soft">{key.replaceAll('_', ' ')}</span>
              <span className="shrink-0 font-mono font-bold text-ink">{count} <span className="text-[9px] font-medium text-ink-faint">({share}%)</span></span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-aeblack-100/80 shadow-inner">
              <div
                className={`h-full min-w-1 rounded-full ${colors[index % colors.length]} transition-all duration-500`}
                style={{ width: `${count > 0 ? Math.max(3, pct) : 0}%` }}
              />
            </div>
          </button>
        );
      })}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-aeblack-100 pt-2 text-[10px] text-ink-faint">
        <span>{rows.length} categories</span>
        <span className="font-mono font-bold">{total} total</span>
      </div>
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  const width = 320;
  const height = 90;
  const baseline = 78;
  const points = values
    .map((value, index) => `${(index / Math.max(1, values.length - 1)) * width},${baseline - (value / max) * 64}`)
    .join(' ');
  const first = values[0] ?? 0;
  const last = values[values.length - 1] ?? 0;
  const delta = last - first;
  const area = `0,${baseline} ${points} ${width},${baseline}`;
  return (
    <div>
      <div className="mb-1 flex items-end justify-between">
        <span className="font-mono text-2xl font-extrabold text-ink">{values.reduce((sum, value) => sum + value, 0)}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${delta >= 0 ? 'bg-aegreen-50 text-approve' : 'bg-aered-50 text-reject'}`}>
          {delta >= 0 ? '+' : ''}{delta} period change
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="14 day application volume trend" className="h-24 w-full overflow-visible">
        <defs>
          <linearGradient id="volume-area" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0190d4" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0190d4" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="volume-line" x1="0" x2="1">
            <stop offset="0%" stopColor="#0173ab" />
            <stop offset="100%" stopColor="#b68a35" />
          </linearGradient>
        </defs>
        {[22, 50, baseline].map((y) => <path key={y} d={`M0 ${y} H${width}`} stroke="#e1e3e5" strokeWidth="1" strokeDasharray={y === baseline ? undefined : '3 5'} />)}
        {values.length > 1 && <polygon points={area} fill="url(#volume-area)" />}
        <polyline points={points} fill="none" stroke="url(#volume-line)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        {values.map((value, index) => {
          const x = (index / Math.max(1, values.length - 1)) * width;
          const y = baseline - (value / max) * 64;
          return <circle key={index} cx={x} cy={y} r={index === values.length - 1 ? 4 : 2.5} fill={index === values.length - 1 ? '#b68a35' : '#ffffff'} stroke="#0173ab" strokeWidth="2"><title>{value} applications</title></circle>;
        })}
      </svg>
    </div>
  );
}

export function DashboardPage() {
  const { l, lang } = useI18n();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [assuranceDetails, setAssuranceDetails] = useState<Partial<Record<AssuranceSlug, AssuranceCommandCenterData>> | null>(null);
  const [error, setError] = useState(false);
  const [audit, setAudit] = useState<{ ok: boolean; rows: number } | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [windowKey, setWindowKey] = useState<WindowKey>('7d');
  const [queueItems, setQueueItems] = useState<QueueItemLite[] | null>(null);
  const [me, setMe] = useState<{ id: string; displayName: string } | null>(null);
  const [needsYou, setNeedsYou] = useState<{
    handover: HandoverLite[] | null;
    approvals: ApprovalLite[] | null;
    drafts: ServiceLite[] | null;
  }>({ handover: null, approvals: null, drafts: null });
  const [activity, setActivity] = useState<AuditItemLite[] | null>(null);

  useEffect(() => {
    admin()
      .dashboard()
      .then((data) => setSummary(data as unknown as Summary))
      .catch(() => setError(true));
  }, []);

  // Cheap parallel reads for the SLA heat strip, the "Needs you" strip and the
  // activity pulse. Each settles independently: a role that cannot read one
  // surface (e.g. audit) never blocks the others.
  useEffect(() => {
    let cancelled = false;
    const client = admin();
    void Promise.allSettled([
      client.me(),
      client.queues(),
      client.handoverList(),
      client.approvals(),
      client.services(),
      client.auditRecent(),
    ]).then(([meResult, queuesResult, handoverResult, approvalsResult, servicesResult, auditResult]) => {
      if (cancelled) return;
      if (meResult.status === 'fulfilled') setMe(meResult.value);
      if (queuesResult.status === 'fulfilled') {
        setQueueItems((queuesResult.value.items ?? []) as unknown as QueueItemLite[]);
      }
      setNeedsYou({
        handover: handoverResult.status === 'fulfilled' ? (handoverResult.value.items ?? []) as unknown as HandoverLite[] : [],
        approvals: approvalsResult.status === 'fulfilled' ? (approvalsResult.value.items ?? []) as unknown as ApprovalLite[] : [],
        drafts: servicesResult.status === 'fulfilled' ? (servicesResult.value.services ?? []) as unknown as ServiceLite[] : [],
      });
      if (auditResult.status === 'fulfilled') {
        setActivity((auditResult.value.items ?? []) as unknown as AuditItemLite[]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAssuranceDetails(null);
    void Promise.allSettled(
      ASSURANCE_AGENTS.map(({ slug }) => admin().assuranceCommandCenter(slug, lang)),
    ).then((results) => {
      if (cancelled) return;
      const next: Partial<Record<AssuranceSlug, AssuranceCommandCenterData>> = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const slug = ASSURANCE_AGENTS[index]!.slug;
          next[slug] = result.value.commandCenter;
        }
      });
      setAssuranceDetails(next);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const verify = async () => {
    setAuditError(null);
    try {
      setAudit(await admin().auditVerify());
    } catch (err) {
      setAuditError((err as Error).message);
    }
  };

  const exportMetrics = async () => {
    try {
      const blob = await admin().metricsCsv();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'servicesai-metrics.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setAuditError((err as Error).message);
    }
  };

  const moveWindow = (event: ReactKeyboardEvent<HTMLButtonElement>, id: WindowKey) => {
    const current = WINDOW_ORDER.indexOf(id);
    const step = lang === 'ar' ? -1 : 1;
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? WINDOW_ORDER.length - 1
          : event.key === 'ArrowRight'
            ? (current + step + WINDOW_ORDER.length) % WINDOW_ORDER.length
            : event.key === 'ArrowLeft'
              ? (current - step + WINDOW_ORDER.length) % WINDOW_ORDER.length
              : -1;
    if (nextIndex < 0) return;
    event.preventDefault();
    const next = WINDOW_ORDER[nextIndex]!;
    setWindowKey(next);
    window.requestAnimationFrame(() => document.getElementById(`dash-window-${next}`)?.focus());
  };

  // Window-scoped datasets. Dated rows are filtered by their own timestamp;
  // a dataset with no dates at all falls back to showing everything.
  const cutoff = Date.now() - WINDOW_MS[windowKey];
  const volumeRows = useMemo(
    () => (summary?.volumeTrend ?? []).filter((row) => new Date(row.day).getTime() >= cutoff),
    [summary, cutoff],
  );
  const volumeSpark = volumeRows.map((row) => row.count);
  const volumeTotal = volumeSpark.reduce((sum, value) => sum + value, 0);

  const queueScoped = useMemo(() => {
    const items = queueItems ?? [];
    const hasDates = items.some((item) => item.submitted_at);
    if (!hasDates) return items;
    return items.filter((item) => !item.submitted_at || new Date(item.submitted_at).getTime() >= cutoff);
  }, [queueItems, cutoff]);

  // SLA risk heat strip segments: one per queue (stage) present in the data.
  const queueSegments = useMemo(() => {
    const byStage = new Map<string, { total: number; overdue: number }>();
    for (const item of queueScoped) {
      const segment = byStage.get(item.stage) ?? { total: 0, overdue: 0 };
      segment.total += 1;
      if (isOverdueItem(item)) segment.overdue += 1;
      byStage.set(item.stage, segment);
    }
    return [...byStage.entries()].map(([stage, counts]) => ({
      stage,
      ...counts,
      share: counts.total > 0 ? counts.overdue / counts.total : 0,
    }));
  }, [queueScoped]);

  // Per-day overdue backlog (bucketed by SLA due day) for the breaches tile.
  const breachSpark = useMemo(() => {
    const days = WINDOW_DAYS[windowKey];
    if (days < 2) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startMs = today.getTime() - (days - 1) * 86_400_000;
    const buckets = Array.from({ length: days }, () => 0);
    for (const item of queueScoped) {
      if (!item.due_at || !isOverdueItem(item)) continue;
      const index = Math.floor((new Date(item.due_at).getTime() - startMs) / 86_400_000);
      if (index >= 0 && index < days) buckets[index]! += 1;
    }
    return buckets;
  }, [queueScoped, windowKey]);

  const myClaims = (needsYou.handover ?? []).filter((item) => me !== null && item.claimedById === me.id);
  const myApprovals = (needsYou.approvals ?? []).filter((item) => me === null || item.maker_name !== me.displayName);
  const myDrafts = (needsYou.drafts ?? []).filter((service) => service.has_draft);
  const needsYouLoaded = needsYou.handover !== null && needsYou.approvals !== null && needsYou.drafts !== null;
  const activityFeed = (activity ?? []).slice(0, 12);

  const stageColumns: DataTableColumn<NonNullable<Summary['stageTat']>[number]>[] = [
    {
      id: 'stage',
      header: l(S.stage),
      sortable: true,
      value: (row) => row.stage,
      render: (row) => <span className="font-bold">{row.stage}</span>,
    },
    {
      id: 'p50',
      header: 'P50 TAT',
      sortable: true,
      value: (row) => Number(row.p50_hours),
      render: (row) => <span className="font-mono font-bold">{row.p50_hours ?? '—'}h</span>,
    },
    {
      id: 'p90',
      header: 'P90 TAT',
      sortable: true,
      value: (row) => Number(row.p90_hours),
      render: (row) => <span className="font-mono font-bold text-gold-deep">{row.p90_hours ?? '—'}h</span>,
    },
    {
      id: 'completed',
      header: l({ en: 'Completed (30d)', ar: 'مكتمل (٣٠ يوم)' }),
      sortable: true,
      value: (row) => row.completed,
      render: (row) => <span className="font-mono">{row.completed}</span>,
    },
  ];

  return (
    <div>
      <HeroBand
        eyebrow={l(S.navOperations)}
        title={l(S.dashboardTitle)}
        subtitle={l({
          en: 'Live operational health across service delivery, human review, containment and platform assurance.',
          ar: 'الحالة التشغيلية المباشرة لتقديم الخدمات والمراجعة البشرية والاحتواء وضمان المنصة.',
        })}
        actions={
          <>
            <SegmentedTabs
              tabs={WINDOW_ORDER.map((id) => ({
                id,
                label: l(id === '24h' ? S.window24h : id === '7d' ? S.window7d : S.window30d),
              }))}
              active={windowKey}
              onChange={(id) => setWindowKey(id as WindowKey)}
              ariaLabel={l(S.timeWindow)}
              idPrefix="dash-window"
              onKeyMove={(event, id) => moveWindow(event, id as WindowKey)}
            />
            <button
              type="button"
              onClick={() => void exportMetrics()}
              className="inline-flex min-h-10 cursor-pointer items-center rounded-xl bg-aegold-500 px-4 text-xs font-extrabold text-aeblack-900 shadow-lg transition hover:brightness-110"
            >
              {l({ en: 'Export audited CSV', ar: 'تصدير CSV مدقق' })}
            </button>
          </>
        }
        metrics={
          summary ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <MetricTile
                dark
                label={l(S.applicationVolume)}
                value={volumeTotal}
                hint={l(S.applicationVolumeHint)}
                accent="#0190d4"
                spark={volumeSpark.length > 1 ? volumeSpark : undefined}
              />
              <MetricTile
                dark
                label={l({ en: 'Open SLA breaches', ar: 'تجاوزات الخدمة المفتوحة' })}
                value={summary.breaches?.total ?? 0}
                hint={`${summary.breaches?.severe ?? 0} > 24h`}
                accent={(summary.breaches?.total ?? 0) > 0 ? '#d83731' : '#3f8e50'}
                spark={breachSpark.length > 1 ? breachSpark : undefined}
              />
              <MetricTile
                dark
                label={l({ en: 'Containment (30d)', ar: 'الاحتواء (٣٠ يوم)' })}
                value={summary.containmentRate === null || summary.containmentRate === undefined ? '—' : `${Math.round(summary.containmentRate * 100)}%`}
                accent="#0173ab"
              />
              <MetricTile
                dark
                label={l(S.overrideRate)}
                value={summary.overrideRate === null ? '—' : `${Math.round(summary.overrideRate * 100)}%`}
                hint={
                  summary.overrideRate === null
                    ? undefined
                    : summary.overrideRate < 0.02
                      ? l({ en: 'Automation-bias alarm', ar: 'تنبيه تحيز للأتمتة' })
                      : summary.overrideRate > 0.3
                        ? l({ en: 'Quality intervention required', ar: 'مطلوب تدخل للجودة' })
                        : l({ en: 'Healthy oversight band', ar: 'نطاق رقابة صحي' })
                }
                accent={
                  summary.overrideRate === null
                    ? '#cba344'
                    : summary.overrideRate < 0.02 || summary.overrideRate > 0.3
                      ? '#d83731'
                      : '#3f8e50'
                }
              />
              <MetricTile
                dark
                label={l(S.decided)}
                value={summary.decisions.decided}
                hint={summary.decisions.avg_hours === null ? undefined : `${l(S.avgHours)}: ${summary.decisions.avg_hours}`}
                accent="#cba344"
              />
              <MetricTile
                dark
                label={l(S.securityScore)}
                value={summary.security?.securityScore ?? '—'}
                accent="#3f8e50"
              />
            </div>
          ) : undefined
        }
      />

      {error && <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{l(S.loadFailed)}</div></div>}

      {/* SLA risk heat strip (plan point 20): one segment per queue, colored by
          overdue share, deep-linking into the filtered queues page. */}
      {queueSegments.length > 0 && (
        <SectionCard accent="#b2550a" title={l(S.slaRiskStrip)} hint={l(S.slaRiskStripHint)} className="mb-4">
          <div className="flex h-16 gap-1" role="group" aria-label={l(S.slaRiskStrip)}>
            {queueSegments.map((segment) => {
              const color = segment.share === 0 ? '#3f8e50' : segment.share < 0.34 ? '#d67907' : '#d83731';
              return (
                <button
                  key={segment.stage}
                  type="button"
                  onClick={() => navigate(`/queues?queue=${encodeURIComponent(segment.stage)}`)}
                  aria-label={`${segment.stage.replaceAll('_', ' ')}: ${segment.overdue} ${l(S.overdue)} / ${segment.total}`}
                  className="grid min-w-16 cursor-pointer place-items-center overflow-hidden px-1 text-white transition hover:brightness-110 focus:outline-2 focus:outline-offset-1 focus:outline-aeblack-800 first:rounded-s-xl last:rounded-e-xl"
                  style={{ flexGrow: segment.total, flexBasis: 0, backgroundColor: color }}
                >
                  <span className="w-full truncate text-center text-[10px] font-extrabold uppercase tracking-wide">
                    {segment.stage.replaceAll('_', ' ')}
                  </span>
                  <span className="font-mono text-[10px] font-bold opacity-90" dir="ltr">
                    {segment.overdue}/{segment.total}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] font-semibold text-ink-faint">
            <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: '#3f8e50' }} />{l(S.slaRiskLegendOk)}</span>
            <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: '#d67907' }} />{l(S.slaRiskLegendWarn)}</span>
            <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: '#d83731' }} />{l(S.slaRiskLegendCritical)}</span>
          </div>
        </SectionCard>
      )}

      {/* "Needs you" personal strip (plan point 21): my claimed handover
          conversations, approvals awaiting my decision, open service drafts. */}
      <SectionCard accent="#3f8e50" title={l(S.needsYou)} hint={l(S.needsYouHint)} className="mb-4">
        <div className="grid gap-3 md:grid-cols-3">
          <NeedsYouList
            title={l(S.myConversations)}
            count={myClaims.length}
            loading={!needsYouLoaded}
            empty={l(S.nothingHere)}
            viewAllTo="/handover"
            items={myClaims.slice(0, 3).map((item) => ({
              key: item.conversationId,
              to: `/handover/${item.conversationId}`,
              label: item.customerName?.[lang] ?? item.customerName?.en ?? item.phone ?? item.conversationId.slice(0, 8),
              meta: item.channel,
            }))}
          />
          <NeedsYouList
            title={l(S.myApprovals)}
            count={myApprovals.length}
            loading={!needsYouLoaded}
            empty={l(S.nothingHere)}
            viewAllTo="/approvals"
            items={myApprovals.slice(0, 3).map((item) => ({
              key: item.id,
              to: '/approvals',
              label: item.kind.replaceAll('_', ' '),
              meta: item.maker_name,
            }))}
          />
          <NeedsYouList
            title={l(S.myDrafts)}
            count={myDrafts.length}
            loading={!needsYouLoaded}
            empty={l(S.nothingHere)}
            viewAllTo="/services"
            items={myDrafts.slice(0, 3).map((service) => ({
              key: service.slug,
              to: `/services/${service.slug}`,
              label: l(service.name),
              meta: service.slug,
              leading: (
                <ServiceIcon
                  slug={service.slug}
                  category={service.category}
                  className="h-8 w-8 rounded-lg p-1"
                />
              ),
            }))}
          />
        </div>
      </SectionCard>

      {summary && (
        <div className="space-y-4">
          {summary.assurance && (
            <SectionCard
              accent="#0173ab"
              title={l({ en: 'Assurance Overview', ar: 'نظرة عامة على الضمان' })}
              hint={l({
                en: 'One evidence-backed view of Cyber, AI Governance, Tech Ops and Language assurance.',
                ar: 'عرض موحد قائم على الأدلة للأمن السيبراني وحوكمة الذكاء الاصطناعي والعمليات التقنية وضمان اللغة.',
              })}
              actions={
                <div className="grid gap-2 text-xs sm:grid-cols-2">
                  <div className="rounded-lg border border-aeblack-100 bg-white px-3 py-2">
                    <div className="text-[10px] font-bold text-ink-faint">{l({ en: 'Open critical items', ar: 'العناصر الحرجة المفتوحة' })}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 font-mono text-lg font-extrabold">
                      <StatusDot tone={summary.assurance.openCritical > 0 ? 'warn' : 'live'} />
                      {summary.assurance.openCritical}
                    </div>
                  </div>
                  <div className="rounded-lg border border-aeblack-100 bg-white px-3 py-2">
                    <div className="text-[10px] font-bold text-ink-faint">{l({ en: 'Report delivery · 7 days', ar: 'تسليم التقارير · ٧ أيام' })}</div>
                    {summary.assurance.reportDelivery.last_finished_at ? (
                      <>
                        <div className="mt-0.5 flex items-center gap-1.5 font-bold">
                          <StatusDot tone={summary.assurance.reportDelivery.failed > 0 ? 'warn' : 'live'} />
                          {summary.assurance.reportDelivery.failed > 0
                            ? l({ en: 'Needs attention', ar: 'يتطلب الانتباه' })
                            : l({ en: 'Delivering', ar: 'يتم التسليم' })}
                        </div>
                        <div className="mt-0.5 text-[10px] text-ink-faint">
                          {summary.assurance.reportDelivery.succeeded} {l({ en: 'delivered', ar: 'مسلّم' })} ·{' '}
                          {summary.assurance.reportDelivery.failed} {l({ en: 'failed', ar: 'فاشل' })}
                        </div>
                        <time dateTime={summary.assurance.reportDelivery.last_finished_at} className="block text-[9px] text-ink-faint">
                          {new Date(summary.assurance.reportDelivery.last_finished_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}
                        </time>
                      </>
                    ) : (
                      <div className="mt-0.5 flex items-center gap-1.5 font-bold text-ink-faint">
                        <StatusDot tone="idle" />
                        {l({ en: 'Not measured', ar: 'غير مقاس' })}
                      </div>
                    )}
                  </div>
                </div>
              }
            >
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {ASSURANCE_AGENTS.map((definition) => {
                  const agent = summary.assurance!.agents.find((candidate) => candidate.slug === definition.slug);
                  const measured = agent?.measurementStatus === 'measured' && agent.score !== null;
                  const commandCenter = assuranceDetails?.[definition.slug];
                  return (
                    <article
                      key={definition.slug}
                      aria-labelledby={`assurance-agent-${definition.slug}`}
                      className="rounded-xl border border-aeblack-100 bg-white p-4"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h3 id={`assurance-agent-${definition.slug}`} className="text-xs font-extrabold">{l(definition.name)}</h3>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-ink-faint">
                          <span aria-hidden>{measured ? '✓' : '—'}</span>
                          {measured ? l({ en: 'Measured', ar: 'مقاس' }) : l({ en: 'Not measured', ar: 'غير مقاس' })}
                        </span>
                      </div>
                      <div className="mt-2 font-mono text-3xl font-extrabold text-ink">
                        {measured ? agent.score : '—'}
                      </div>
                      <div className="min-h-4 text-[10px] text-ink-faint">
                        {agent?.scoreLabel ? l(agent.scoreLabel) : l({ en: 'Assurance state', ar: 'حالة الضمان' })}
                      </div>
                      {agent?.observedAt && (
                        <time dateTime={agent.observedAt} className="mt-1 block text-[9px] text-ink-faint">
                          {l({ en: 'Observed ', ar: 'تم الرصد ' })}
                          {new Date(agent.observedAt).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}
                        </time>
                      )}
                      {agent?.summary && (
                        <p className="mt-3 line-clamp-3 text-xs text-ink-soft">{l(agent.summary)}</p>
                      )}
                      {agent?.recommendations[0] ? (
                        <p className="mt-2 rounded-lg bg-seablue-50 p-2 text-[11px] text-oasis">
                          <span className="font-bold">{l({ en: 'AI recommendation: ', ar: 'توصية الذكاء الاصطناعي: ' })}</span>
                          {l(agent.recommendations[0])}
                        </p>
                      ) : (
                        <p className="mt-2 text-[10px] text-ink-faint">{l({ en: 'No run-backed recommendation available.', ar: 'لا تتوفر توصية مستندة إلى تشغيل.' })}</p>
                      )}
                      <div className="mt-3 border-t border-aeblack-50 pt-2">
                        {assuranceDetails === null ? (
                          <p className="text-[10px] text-ink-faint">{l({ en: 'Loading measured trends…', ar: 'جارٍ تحميل الاتجاهات المقاسة…' })}</p>
                        ) : (
                          <>
                            <AssuranceMiniTrend rows={commandCenter?.trend ?? []} name={l(definition.name)} />
                            <dl className="grid grid-cols-3 gap-1 text-center">
                              {ASSURANCE_WINDOWS.map((window) => {
                                const value = assuranceWindowValue(commandCenter, window.minutes);
                                const activeWindow = window.minutes === WINDOW_ASSURANCE_MINUTES[windowKey];
                                return (
                                  <div
                                    key={window.minutes}
                                    className={`rounded bg-aeblack-50 p-1 ${activeWindow ? 'ring-2 ring-seablue-400' : ''}`}
                                  >
                                    <dt className="text-[9px] font-bold text-ink-faint">{l(window.label)}</dt>
                                    <dd className="font-mono text-[11px] font-extrabold">{value ?? '—'}</dd>
                                  </div>
                                );
                              })}
                            </dl>
                          </>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
                        <Link
                          to={`/agents/${definition.slug}?tab=overview#assurance-trends`}
                          className="min-h-10 py-2 text-xs font-bold text-oasis underline-offset-2 hover:underline focus:outline-2 focus:outline-gold/70"
                        >
                          {l({ en: 'Trends & incidents', ar: 'الاتجاهات والحوادث' })}
                        </Link>
                        <Link
                          to={`/agents/${definition.slug}?tab=reporting`}
                          className="min-h-10 py-2 text-xs font-bold text-oasis underline-offset-2 hover:underline focus:outline-2 focus:outline-gold/70"
                        >
                          {l({ en: 'Reports', ar: 'التقارير' })}
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>
            </SectionCard>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <SectionCard accent="#0173ab" title={l(S.byState)}>
              <BarChart rows={summary.byState as { state: string; count: number }[]} labelKey="state" />
            </SectionCard>
            <SectionCard accent="#0173ab" title={l(S.queueDepth)}>
              <BarChart rows={summary.queueDepth as { stage: string; count: number }[]} labelKey="stage" />
            </SectionCard>
            {summary.byChannel && summary.byChannel.length > 0 && (
              <SectionCard accent="#0173ab" title={l(S.byChannel)}>
                <BarChart rows={summary.byChannel} labelKey="channel" />
              </SectionCard>
            )}
            <SectionCard
              accent="#cba344"
              title={`${l(S.applicationVolume)} · ${l(windowKey === '24h' ? S.window24h : windowKey === '7d' ? S.window7d : S.window30d)}`}
              actions={
                <span className="inline-flex items-center gap-1.5 rounded-full bg-aegreen-50 px-2.5 py-1 text-[10px] font-extrabold text-approve">
                  <StatusDot tone="live" />
                  {l({ en: 'Live', ar: 'مباشر' })}
                </span>
              }
            >
              <Sparkline values={volumeSpark} />
            </SectionCard>
            {summary.notCompleted && (
              <SectionCard accent="#0173ab" title={l(S.notCompleted)} className="md:col-span-2">
                <div className="flex gap-10">
                  <div><div className="text-[11px] text-ink-faint">{l(S.draftsInProgress)}</div><div className="font-mono text-3xl font-extrabold">{summary.notCompleted.drafts}</div></div>
                  <div><div className="text-[11px] text-ink-faint">{l(S.abandoned)}</div><div className={`font-mono text-3xl font-extrabold ${summary.notCompleted.abandoned > 0 ? 'text-gold-deep' : ''}`}>{summary.notCompleted.abandoned}</div></div>
                </div>
              </SectionCard>
            )}
            {summary.security && (
              <SectionCard accent="#0173ab" title={l(S.securityPanel)} className="md:col-span-2">
                <div className="grid gap-4 sm:grid-cols-3">
                  <MetricCard label={l(S.openIncidents)} value={summary.security.openIncidents} tone={summary.security.openIncidents > 0 ? 'danger' : 'success'} />
                  <div><div className="mb-1 text-[11px] text-ink-faint">{l(S.securityEvents24h)}</div><BarChart rows={summary.security.events24h} labelKey="severity" /></div>
                  <div><div className="mb-1 text-[11px] text-ink-faint">{l(S.moderation7d)}</div><BarChart rows={summary.security.moderation7d} labelKey="action" /></div>
                </div>
              </SectionCard>
            )}
          </div>

          <SectionCard
            accent="#0173ab"
            title={l({ en: 'Stage turnaround distribution', ar: 'توزيع زمن الإنجاز حسب المرحلة' })}
            hint={l({ en: 'P50 and P90 from completed assignments in the last 30 days.', ar: 'القيمتان P50 وP90 من المهام المكتملة خلال آخر ٣٠ يوماً.' })}
          >
            <DataTable
              rows={summary.stageTat ?? []}
              columns={stageColumns}
              rowKey={(row) => row.stage}
              emptyTitle={l({ en: 'No completed stage data', ar: 'لا توجد بيانات مراحل مكتملة' })}
              filename="stage-tat.csv"
            />
          </SectionCard>

          {(summary.overrideByService?.length ?? 0) > 0 && (
            <SectionCard accent="#0173ab" title={l({ en: 'Override health by service', ar: 'صحة التجاوزات حسب الخدمة' })}>
              <div className="space-y-2">
                {summary.overrideByService!.map((row) => {
                  const pct = Math.round((row.rate ?? 0) * 100);
                  const alarm = row.rate !== null && (row.rate < 0.02 || row.rate > 0.3);
                  return (
                    <div key={row.slug} className="grid items-center gap-2 text-xs sm:grid-cols-[minmax(0,1fr)_10rem_4rem]">
                      <span className="flex min-w-0 items-center gap-2">
                        <ServiceIcon slug={row.slug} className="h-8 w-8 rounded-lg p-1" />
                        <span className="truncate font-semibold">{l(row.name)}</span>
                      </span>
                      <div className="h-2 overflow-hidden rounded-full bg-aeblack-100"><div className={`h-full ${alarm ? 'bg-reject' : 'bg-approve'}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
                      <span className={`font-mono font-bold ${alarm ? 'text-reject' : ''}`}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* Activity pulse (plan point 23): latest audited platform events grouped
          by hour. Hidden entirely when the staff role cannot read the audit feed. */}
      {activity !== null && (
        <SectionCard accent="#0173ab" title={l(S.activityPulse)} hint={l(S.activityPulseHint)} className="mt-4">
          {activityFeed.length === 0 ? (
            <p className="py-4 text-center text-xs text-ink-faint">{l(S.activityEmpty)}</p>
          ) : (
            <ol>
              {activityFeed.map((item, index) => {
                const date = new Date(item.ts);
                const hourKey = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours()).getTime();
                const previous = activityFeed[index - 1];
                const previousDate = previous ? new Date(previous.ts) : null;
                const previousHourKey = previousDate
                  ? new Date(previousDate.getFullYear(), previousDate.getMonth(), previousDate.getDate(), previousDate.getHours()).getTime()
                  : null;
                const showSeparator = hourKey !== previousHourKey;
                return (
                  <li key={item.id}>
                    {showSeparator && (
                      <div aria-hidden className={`flex items-center gap-2 ${index === 0 ? '' : 'pt-2'}`}>
                        <span className="h-px flex-1 bg-aeblack-100" />
                        <time className="text-[9px] font-bold uppercase tracking-wide text-ink-faint" dateTime={item.ts}>
                          {new Intl.DateTimeFormat(lang === 'ar' ? 'ar-AE' : 'en-GB', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          }).format(date)}
                        </time>
                        <span className="h-px flex-1 bg-aeblack-100" />
                      </div>
                    )}
                    <div className="flex items-center gap-3 py-1.5">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-seablue-50 text-oasis">
                        <Icon name={activityIcon(item.action)} size={14} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <code className="block truncate text-[11px] font-bold">{item.action}</code>
                        <span className="text-[10px] text-ink-faint">
                          {String(item.actor.kind ?? 'system')} · {String(item.subject.table ?? '—')}
                        </span>
                      </div>
                      <time className="shrink-0 text-[10px] font-semibold text-ink-faint" dateTime={item.ts}>
                        {relativeTime(item.ts, lang)}
                      </time>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </SectionCard>
      )}

      <GlassCard className="mt-4 flex flex-wrap items-center gap-3 p-5" strong>
        <GlassButton onClick={() => void verify()}>{l(S.auditVerify)}</GlassButton>
        {audit && (
          <span className={`text-sm font-bold ${audit.ok ? 'text-approve' : 'text-reject'}`}>
            {audit.ok ? l(S.auditOk) : l(S.auditBroken)} — {audit.rows} {l(S.rows)}
          </span>
        )}
      {auditError && <span className="text-xs font-semibold text-reject">{auditError}</span>}
    </GlassCard>
    </div>
  );
}

/** One "Needs you" mini-list: count badge, top-3 linked rows, view-all link. */
function NeedsYouList({
  title,
  count,
  loading,
  empty,
  viewAllTo,
  items,
}: {
  title: string;
  count: number;
  loading: boolean;
  empty: string;
  viewAllTo: string;
  items: { key: string; to: string; label: string; meta: string; leading?: ReactNode }[];
}) {
  const { l } = useI18n();
  return (
    <div className="rounded-xl border border-aeblack-100 bg-aeblack-50/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-extrabold">{title}</h3>
        <span className="rounded-full bg-white px-2 py-0.5 font-mono text-[10px] font-extrabold shadow-sm">{count}</span>
      </div>
      {loading ? (
        <div className="mt-2">
          <TableSkeleton rows={3} columns={2} />
        </div>
      ) : (
        <>
          <ul className="mt-2 min-h-16 space-y-1">
            {items.length === 0 && <li className="py-2 text-[11px] text-ink-faint">{empty}</li>}
            {items.map((item) => (
              <li key={item.key}>
                <Link
                  to={item.to}
                  className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] font-semibold text-ink transition hover:bg-white focus:outline-2 focus:outline-gold/70"
                >
                  {item.leading}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{item.label}</span>
                    <span className="block truncate text-[9px] font-medium text-ink-faint">{item.meta}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to={viewAllTo}
            className="mt-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-bold text-oasis underline-offset-2 hover:underline focus:outline-2 focus:outline-gold/70"
          >
            {l(S.viewAll)}
            <svg aria-hidden width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="rtl:-scale-x-100">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </>
      )}
    </div>
  );
}
